const SLOT_STEP = 0.5; // 30 minutes

export const MAX_INTERVALS_PER_DAY = 2;

export const WORKING_DAY_ITEMS = [
  { key: "monday", label: "Luni" },
  { key: "tuesday", label: "Marți" },
  { key: "wednesday", label: "Miercuri" },
  { key: "thursday", label: "Joi" },
  { key: "friday", label: "Vineri" },
  { key: "saturday", label: "Sâmbătă" },
  { key: "sunday", label: "Duminică" },
];

export const DAY_LABEL_TO_KEY = WORKING_DAY_ITEMS.reduce((acc, item) => {
  acc[item.label] = item.key;
  return acc;
}, {});

export const DAY_KEY_TO_LABEL = WORKING_DAY_ITEMS.reduce((acc, item) => {
  acc[item.key] = item.label;
  return acc;
}, {});

const defaultIntervals = () => [{ start: "09:00", end: "20:30" }];
const defaultWeekendIntervals = () => [];

export const defaultWorkingHours = () => ({
  monday: { enabled: true, intervals: defaultIntervals() },
  tuesday: { enabled: true, intervals: defaultIntervals() },
  wednesday: { enabled: true, intervals: defaultIntervals() },
  thursday: { enabled: true, intervals: defaultIntervals() },
  friday: { enabled: true, intervals: defaultIntervals() },
  saturday: { enabled: false, intervals: defaultWeekendIntervals() },
  sunday: { enabled: false, intervals: defaultWeekendIntervals() },
});

export const parseTimeString = (value) => {
  if (!value || typeof value !== "string") return null;
  const match = value.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!match) return null;
  const hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);
  return hours + minutes / 60;
};

export const formatHourLabel = (hour) => {
  const hh = String(Math.floor(hour)).padStart(2, "0");
  const mm = hour % 1 === 0 ? "00" : "30";
  return `${hh}:${mm}`;
};

const clampToStep = (value) => Math.round(value / SLOT_STEP) * SLOT_STEP;

const normalizeIntervals = (intervals) => {
  if (!Array.isArray(intervals)) {
    return [];
  }
  return intervals
    .slice(0, MAX_INTERVALS_PER_DAY)
    .map((interval) => ({
      start: typeof interval?.start === "string" ? interval.start : "",
      end: typeof interval?.end === "string" ? interval.end : "",
    }))
    .filter((interval) => parseTimeString(interval.start) !== null && parseTimeString(interval.end) !== null);
};

export const withDefaultWorkingHours = (maybeConfig) => {
  const defaults = defaultWorkingHours();
  if (!maybeConfig || typeof maybeConfig !== "object") return defaults;

  const merged = {};
  WORKING_DAY_ITEMS.forEach(({ key }) => {
    const source = maybeConfig[key] || {};
    const fallback = defaults[key];
    const enabled =
      typeof source.enabled === "boolean" ? source.enabled : fallback.enabled;
    let providedIntervals = source.intervals || source.intervalList || source.slots;
    if (!Array.isArray(providedIntervals) && source.start && source.end) {
      providedIntervals = [{ start: source.start, end: source.end }];
    }
    const normalized = normalizeIntervals(providedIntervals);
    const intervals = enabled
      ? normalized.length
        ? normalized
        : fallback.intervals.map((interval) => ({ ...interval }))
      : [];
    merged[key] = {
      enabled,
      intervals,
    };
  });
  return merged;
};

const getAllIntervals = (workingHours) =>
  WORKING_DAY_ITEMS.flatMap(({ key }) => (workingHours?.[key]?.intervals || []));

export const computeHourWindow = ({
  workingHours,
  minFallback = 9,
  maxFallback = 20.5,
  appointments = [],
}) => {
  let minHour = Number.POSITIVE_INFINITY;
  let maxHour = Number.NEGATIVE_INFINITY;

  getAllIntervals(workingHours).forEach((interval) => {
    const start = parseTimeString(interval.start);
    const end = parseTimeString(interval.end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return;
    minHour = Math.min(minHour, start);
    maxHour = Math.max(maxHour, end - SLOT_STEP);
  });

  appointments.forEach((slot) => {
    if (!slot?.hour && slot?.hour !== 0) return;
    const slotHour = Number(slot.hour);
    if (!Number.isFinite(slotHour)) return;
    minHour = Math.min(minHour, slotHour);
    maxHour = Math.max(maxHour, slotHour);
  });

  if (!Number.isFinite(minHour)) minHour = minFallback;
  if (!Number.isFinite(maxHour)) maxHour = maxFallback;

  const start = Math.max(0, clampToStep(Math.floor(minHour / SLOT_STEP) * SLOT_STEP));
  const end = clampToStep(Math.ceil((maxHour + SLOT_STEP) / SLOT_STEP) * SLOT_STEP - SLOT_STEP);

  if (end < start) {
    return { start: minFallback, end: maxFallback };
  }

  return { start, end };
};

export const buildHourSlots = ({ start, end, step = SLOT_STEP }) => {
  const safeStart = typeof start === "number" ? start : 9;
  const safeEnd = typeof end === "number" ? end : 20.5;
  const total = Math.max(1, Math.round((safeEnd - safeStart) / step) + 1);
  return Array.from({ length: total }, (_, idx) => Number((safeStart + idx * step).toFixed(4)));
};

export const isSlotWithinWorkingHours = (workingHours, dayLabel, hour, duration = SLOT_STEP) => {
  const key = DAY_LABEL_TO_KEY[dayLabel];
  if (!key) return true;
  const cfg = workingHours?.[key];
  if (!cfg || cfg.enabled === false) return false;
  const slotEnd = hour + duration;
  return (cfg.intervals || []).some((interval) => {
    const start = parseTimeString(interval.start);
    const end = parseTimeString(interval.end);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return false;
    return hour >= start && slotEnd <= end + 1e-6;
  });
};

export const describeDaySchedule = (workingHours, dayLabel) => {
  const key = DAY_LABEL_TO_KEY[dayLabel];
  const cfg = workingHours?.[key];
  if (!cfg) {
    return { enabled: true, rangeLabel: "" };
  }
  if (cfg.enabled === false) {
    return { enabled: false, rangeLabel: "Zi liberă" };
  }

  const segments = (cfg.intervals || []).map((interval) => {
    const start = interval.start || "–";
    const end = interval.end || "–";
    return `${start}–${end}`;
  });

  return {
    enabled: true,
    rangeLabel: segments.length ? segments.join(" + ") : "",
  };
};

export const SLOT_DURATION = SLOT_STEP;
